import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { t, getLanguage } from './translations';
import { initializeTurkishFont, ensureUTF8, isRobotoLoaded } from './fontLoader';
import { formatCurrency } from './currencyFormatter';

// Helper function to draw rounded rectangle
// jsPDF 2.5.1+ supports roundedRect method
const drawRoundedRect = (doc, x, y, width, height, radius, fillStroke = 'FD') => {
  // Use roundedRect if available (jsPDF 2.0+)
  if (typeof doc.roundedRect === 'function') {
    doc.roundedRect(x, y, width, height, radius, radius, fillStroke);
  } else {
    // Fallback: regular rectangle if roundedRect not available
    doc.rect(x, y, width, height, fillStroke);
  }
};

// Helper function to ensure Turkish characters are properly handled
// jsPDF 2.5.1 has better Unicode support, but we ensure proper encoding
const safeText = (text) => {
  if (!text) return text;
  // Ensure text is properly UTF-8 encoded for Turkish characters
  return ensureUTF8(String(text));
};

// Helper function to encode Turkish characters properly
const encodeTurkishText = (text) => {
  if (!text) return text;
  // jsPDF handles most Unicode characters, but we ensure proper encoding
  // Convert to string and ensure proper encoding
  return String(text)
    .replace(/ı/g, 'ı')
    .replace(/İ/g, 'İ')
    .replace(/ğ/g, 'ğ')
    .replace(/Ğ/g, 'Ğ')
    .replace(/ş/g, 'ş')
    .replace(/Ş/g, 'Ş')
    .replace(/ü/g, 'ü')
    .replace(/Ü/g, 'Ü')
    .replace(/ö/g, 'ö')
    .replace(/Ö/g, 'Ö')
    .replace(/ç/g, 'ç')
    .replace(/Ç/g, 'Ç');
};

// Helper function to get the appropriate font name
// Uses 'roboto' if loaded, otherwise falls back to 'times'
const getFont = (style = 'normal') => {
  if (isRobotoLoaded()) {
    return 'roboto';
  }
  return 'times';
};

export const generatePDF = async (proposalData) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Create PDF with proper encoding for Turkish characters
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true,
        floatPrecision: 'smart'
      });
      
      // Initialize font support for Turkish characters
      // Using 'times' font which has better Unicode support than 'helvetica'
      // Note: For FULL Turkish character support (ç, ğ, ı, ö, ş, ü, İ, Ş, Ğ, Ü, Ö, Ç),
      // you need to add a custom TTF font. See fontLoader.js for instructions.
      await initializeTurkishFont(doc);
      
      // Modern color scheme - Purple theme
      const primaryColor = [139, 92, 246]; // Purple
      const lightPurple = [196, 181, 253]; // Light purple for section headers
      const textColor = [30, 30, 30]; // Dark text
      const lightText = [107, 114, 128]; // Gray text
      
      // Page margins
      const marginX = 15;
      const marginY = 15;
      let yPos = marginY;
      
      // Header Section
      // Logo (if provided) - top left, replaces "PROPOSAL" text
      if (proposalData.logo) {
        try {
          // Add logo image (smaller size: 25x25mm)
          const logoWidth = 25;
          const logoHeight = 25;
          const logoX = marginX;
          const logoY = marginY - 5; // Move logo higher up
          
          doc.addImage(proposalData.logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
        } catch (error) {
          console.error('Error adding logo to PDF:', error);
          // Fallback to text if logo fails
          doc.setFontSize(28);
          doc.setFont(getFont(), 'bold');
          doc.setTextColor(...textColor);
          doc.text(safeText(t('proposal')), marginX, yPos + 10);
        }
      } else {
        // Show "PROPOSAL" text if no logo
        doc.setFontSize(28);
        doc.setFont(getFont(), 'bold');
        doc.setTextColor(...textColor);
        doc.text(safeText(t('proposal')), marginX, yPos + 10);
      }
      
      // Proposal details on the right
      doc.setFontSize(10);
      doc.setFont(getFont(), 'normal');
      doc.setTextColor(...lightText);
      const detailsX = 150;
      let detailsY = yPos + 5;
      
      if (proposalData.proposalNumber) {
        doc.text(safeText(`${t('proposalNo')} #${proposalData.proposalNumber}`), detailsX, detailsY);
        detailsY += 5;
      }
      
      doc.text(safeText(`${t('proposalDate')}: ${proposalData.date}`), detailsX, detailsY);
      detailsY += 5;
      
      if (proposalData.validUntil) {
        const lang = getLanguage();
        let locale = 'en-US';
        if (lang === 'tr') locale = 'tr-TR';
        else if (lang === 'fr') locale = 'fr-FR';
        const validDate = new Date(proposalData.validUntil).toLocaleDateString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        doc.text(safeText(`${t('dueDate')}: ${validDate}`), detailsX, detailsY);
      }
      
      yPos = 40;
      
      // Proposed By Section (Left Card)
      const cardPadding = 8;
      const cardWidth = 85;
      const leftCardX = marginX;
      const leftCardY = yPos;
      
      // Calculate required card height based on content
      let leftCardContentHeight = cardPadding + 12; // Title space
      if (proposalData.companyName) {
        const nameLines = doc.splitTextToSize(proposalData.companyName || '', cardWidth - (cardPadding * 2));
        leftCardContentHeight += (nameLines.length * 4.5) + 2;
      }
      if (proposalData.companyAddress) {
        const addressLines = doc.splitTextToSize(proposalData.companyAddress || '', cardWidth - (cardPadding * 2));
        leftCardContentHeight += (addressLines.length * 4) + 3;
      }
      if (proposalData.companyEmail) {
        leftCardContentHeight += 4;
      }
      if (proposalData.companyPhone) {
        leftCardContentHeight += 4;
      }
      const leftCardHeight = Math.max(50, leftCardContentHeight + cardPadding); // Minimum 50, or content height
      
      // Card background (white with border) - rounded
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.5);
      drawRoundedRect(doc, leftCardX, leftCardY, cardWidth, leftCardHeight, 3, 'FD');
      
      // Section title
      doc.setFontSize(11);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...primaryColor);
      doc.text(safeText(t('billedBy')), leftCardX + cardPadding, leftCardY + cardPadding + 5);
      
      // Company details
      doc.setFontSize(10);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...textColor);
      let cardY = leftCardY + cardPadding + 12;
      
      if (proposalData.companyName) {
        const nameLines = doc.splitTextToSize(proposalData.companyName, cardWidth - (cardPadding * 2));
        doc.text(nameLines, leftCardX + cardPadding, cardY);
        cardY += (nameLines.length * 4.5) + 2;
      }
      
      doc.setFont(getFont(), 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...lightText);
      
      if (proposalData.companyAddress) {
        const addressLines = doc.splitTextToSize(proposalData.companyAddress, cardWidth - (cardPadding * 2));
        doc.text(addressLines, leftCardX + cardPadding, cardY);
        cardY += (addressLines.length * 4) + 3;
      }
      
      if (proposalData.companyEmail) {
        const emailText = `${t('email')} ${proposalData.companyEmail}`;
        const emailLines = doc.splitTextToSize(emailText, cardWidth - (cardPadding * 2));
        doc.text(emailLines, leftCardX + cardPadding, cardY);
        cardY += (emailLines.length * 4) + 2;
      }
      
      if (proposalData.companyPhone) {
        const phoneText = `${t('phone')} ${proposalData.companyPhone}`;
        const phoneLines = doc.splitTextToSize(phoneText, cardWidth - (cardPadding * 2));
        doc.text(phoneLines, leftCardX + cardPadding, cardY);
      }
      
      // Proposed To Section (Right Card)
      const rightCardX = 110;
      const rightCardY = yPos;
      
      // Calculate required card height based on content
      let rightCardContentHeight = cardPadding + 12; // Title space
      if (proposalData.clientName) {
        const clientNameLines = doc.splitTextToSize(proposalData.clientName || '', cardWidth - (cardPadding * 2));
        rightCardContentHeight += (clientNameLines.length * 4.5) + 2;
      }
      if (proposalData.clientCompany) {
        const companyLines = doc.splitTextToSize(proposalData.clientCompany || '', cardWidth - (cardPadding * 2));
        rightCardContentHeight += (companyLines.length * 4.5) + 2;
      }
      if (proposalData.clientEmail) {
        rightCardContentHeight += 4;
      }
      const rightCardHeight = Math.max(50, rightCardContentHeight + cardPadding); // Minimum 50, or content height
      
      // Card background (white with border) - rounded
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(255, 255, 255); // White fill
      doc.setLineWidth(0.5);
      drawRoundedRect(doc, rightCardX, rightCardY, cardWidth, rightCardHeight, 3, 'FD');
      
      // Section title
      doc.setFontSize(11);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...primaryColor);
      doc.text(safeText(t('billedTo')), rightCardX + cardPadding, rightCardY + cardPadding + 5);
      
      // Client details
      doc.setFontSize(10);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...textColor);
      cardY = rightCardY + cardPadding + 12;
      
      if (proposalData.clientName) {
        const clientNameText = safeText(proposalData.clientName);
        const clientNameLines = doc.splitTextToSize(clientNameText, cardWidth - (cardPadding * 2));
        doc.text(clientNameLines, rightCardX + cardPadding, cardY);
        cardY += (clientNameLines.length * 4.5) + 2;
      }
      
      if (proposalData.clientCompany) {
        const companyText = safeText(proposalData.clientCompany);
        const companyLines = doc.splitTextToSize(companyText, cardWidth - (cardPadding * 2));
        doc.text(companyLines, rightCardX + cardPadding, cardY);
        cardY += (companyLines.length * 4.5) + 2;
      }
      
      doc.setFont(getFont(), 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...lightText);
      
      if (proposalData.clientEmail) {
        const clientEmailText = safeText(`${t('email')} ${proposalData.clientEmail}`);
        const clientEmailLines = doc.splitTextToSize(clientEmailText, cardWidth - (cardPadding * 2));
        doc.text(clientEmailLines, rightCardX + cardPadding, cardY);
      }
      
      // Move to next section - use the taller card height
      yPos = Math.max(leftCardY + leftCardHeight, rightCardY + rightCardHeight) + 15;
      
      // Description section (if provided)
      if (proposalData.proposalDescription) {
        doc.setFontSize(11);
        doc.setFont(getFont(), 'bold');
        doc.setTextColor(...textColor);
        doc.text(safeText(t('description')), marginX, yPos);
        yPos += 7;
        
        doc.setFontSize(9);
        doc.setFont(getFont(), 'normal');
        doc.setTextColor(...lightText);
        const descText = safeText(proposalData.proposalDescription);
        const descLines = doc.splitTextToSize(descText, 180);
        doc.text(descLines, marginX, yPos);
        yPos += (descLines.length * 4.5) + 10;
      } else {
        yPos += 5;
      }
      
      // Table starts directly (hero band and description removed)
      
      // Service Table with format: Description | Quantity | Unit | Unit Price | Calculation (1 unit x unit price x quantity = total)
      const tableData = proposalData.items.map(item => {
        const quantity = item.quantity || 1;
        const unit = item.unit || 'ton';
        const unitPrice = item.price || 0;
        const total = quantity * unitPrice;
        const calculation = formatCurrency(total); // Only show the total (multiplication result)
        return [
          safeText(item.description || ''),
          quantity.toString(),
          safeText(unit),
          formatCurrency(unitPrice),
          safeText(calculation)
        ];
      });
      
      doc.autoTable({
        startY: yPos,
        head: [[safeText(t('description')), safeText(t('qty')), safeText(t('unit')), safeText(t('unitPrice')), safeText(t('calculation'))]],
        body: tableData,
        theme: 'plain',
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          font: getFont() // Use roboto if loaded, otherwise times
        },
        bodyStyles: {
          textColor: textColor,
          fontSize: 9,
          font: getFont() // Use roboto if loaded, otherwise times
        },
        styles: {
          cellPadding: 2, // Reduced padding to make cells shorter in height
          lineColor: [229, 231, 235],
          lineWidth: 0.5,
          fontSize: 9,
          font: getFont() // Use roboto if loaded, otherwise times
        },
        columnStyles: {
          0: { cellWidth: 80 }, // Description column - wider
          1: { halign: 'center', cellWidth: 18 }, // Quantity column
          2: { halign: 'center', cellWidth: 18 }, // Unit column
          3: { halign: 'right', cellWidth: 28 }, // Unit Price column
          4: { halign: 'right', cellWidth: 25, fontStyle: 'bold' } // Total column - smaller
        },
        margin: { left: marginX, right: marginX }
      });
      
      // Summary Section
      const finalY = doc.lastAutoTable.finalY + 10;
      const summaryX = 130;
      // Calculate table right edge: marginX + sum of column widths
      // Column widths: 80 + 18 + 18 + 28 + 25 = 169mm
      const tableRightEdge = marginX + 80 + 18 + 18 + 28 + 25; // 15 + 169 = 184mm
      
      doc.setFontSize(9);
      doc.setFont(getFont(), 'normal');
      doc.setTextColor(...lightText);
      
      let summaryY = finalY;
      doc.text(safeText(t('subTotal')), summaryX, summaryY);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...textColor);
      doc.text(formatCurrency(proposalData.total), tableRightEdge, summaryY, { align: 'right' });
      
      summaryY += 5;
      doc.setFont(getFont(), 'normal');
      doc.setTextColor(...lightText);
      doc.text(safeText(t('discount')), summaryX, summaryY);
      doc.text(formatCurrency(0), tableRightEdge, summaryY, { align: 'right' });
      
      summaryY += 5;
      doc.text(safeText(t('vat')), summaryX, summaryY);
      doc.text(formatCurrency(0), tableRightEdge, summaryY, { align: 'right' });
      
      summaryY += 8;
      doc.setFontSize(11);
      doc.setFont(getFont(), 'bold');
      doc.setTextColor(...textColor);
      doc.text(safeText(t('totalAmount')), summaryX, summaryY);
      doc.setFontSize(12);
      doc.text(formatCurrency(proposalData.total), tableRightEdge, summaryY, { align: 'right' });
      
      // Terms & Conditions (if provided)
      if (proposalData.terms) {
        summaryY += 20;
        if (summaryY > 250) {
          doc.addPage();
          summaryY = 20;
        }
        doc.setFontSize(10);
        doc.setFont(getFont(), 'bold');
        doc.setTextColor(...textColor);
        doc.text(safeText(t('termsConditions')), marginX, summaryY);
        summaryY += 6;
        doc.setFontSize(9);
        doc.setFont(getFont(), 'normal');
        doc.setTextColor(...lightText);
        const termsText = safeText(proposalData.terms);
        const termsLines = doc.splitTextToSize(termsText, 180);
        doc.text(termsLines, marginX, summaryY);
      }

      // Bank Account Details (if provided) - Bottom left in a rectangle
      if (proposalData.bankInfo && (
        proposalData.bankInfo.bankName || 
        proposalData.bankInfo.accountHolderName || 
        proposalData.bankInfo.accountNumber ||
        proposalData.bankInfo.iban ||
        proposalData.bankInfo.swift ||
        proposalData.bankInfo.branchName
      )) {
        // Calculate bank info position - place it near bottom of page
        // Page height is 297mm (A4), leave margin at bottom
        const pageHeight = 297;
        const bottomMargin = 5; // Smaller margin
        const maxY = pageHeight - bottomMargin; // Maximum Y position before going off page
        
        // Calculate required height for bank box (count fields) - smaller height
        let fieldCount = 0;
        if (proposalData.bankInfo.bankName) fieldCount++;
        if (proposalData.bankInfo.accountHolderName) fieldCount++;
        if (proposalData.bankInfo.accountNumber) fieldCount++;
        if (proposalData.bankInfo.iban) fieldCount++;
        if (proposalData.bankInfo.swift) fieldCount++;
        if (proposalData.bankInfo.branchName) fieldCount++;
        
        // Smaller height - reduced spacing
        const bankBoxHeight = 10 + (fieldCount * 4) + 2; // Title + fields + padding (reduced)
        // Position it near the bottom of the page
        let bankY = maxY - bankBoxHeight - 3; // Position 3mm from bottom
        
        // If it overlaps with summary, move to new page
        if (bankY < summaryY + 15) {
          doc.addPage();
          bankY = maxY - bankBoxHeight - 3; // Position at bottom of new page
        }

        // Bank info box dimensions
        const bankBoxWidth = 90;
        const bankBoxX = marginX;
        const bankBoxY = bankY;

        // Draw rounded rectangle for bank info
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(255, 255, 255);
        doc.setLineWidth(0.5);
        drawRoundedRect(doc, bankBoxX, bankBoxY, bankBoxWidth, bankBoxHeight, 3, 'FD');

        // Bank info title - smaller
        doc.setFontSize(9);
        doc.setFont(getFont(), 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(safeText(t('bankAccountDetails')), bankBoxX + 4, bankBoxY + 6);

        // Bank info content - compact formatting with smaller spacing
        doc.setFontSize(7); // Smaller font
        doc.setFont(getFont(), 'normal');
        doc.setTextColor(...textColor);
        let bankTextY = bankBoxY + 9; // Reduced top padding
        const bankPadding = 4; // Reduced padding
        const labelWidth = 30; // Slightly smaller label width
        const valueX = bankBoxX + bankPadding + labelWidth;
        const valueWidth = bankBoxWidth - labelWidth - (bankPadding * 2); // Available width for values

        if (proposalData.bankInfo.bankName) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('bankNameLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const bankNameText = safeText(proposalData.bankInfo.bankName);
          const bankNameLines = doc.splitTextToSize(bankNameText, valueWidth);
          doc.text(bankNameLines, valueX, bankTextY);
          bankTextY += Math.max(3.5, bankNameLines.length * 3.5) + 0.5; // Reduced spacing
        }
        if (proposalData.bankInfo.accountHolderName) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('accountHolderLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const holderText = safeText(proposalData.bankInfo.accountHolderName);
          const holderLines = doc.splitTextToSize(holderText, valueWidth);
          doc.text(holderLines, valueX, bankTextY);
          bankTextY += Math.max(3.5, holderLines.length * 3.5) + 0.5;
        }
        if (proposalData.bankInfo.accountNumber) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('accountNoLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const accountText = safeText(proposalData.bankInfo.accountNumber);
          const accountLines = doc.splitTextToSize(accountText, valueWidth);
          doc.text(accountLines, valueX, bankTextY);
          bankTextY += Math.max(3.5, accountLines.length * 3.5) + 0.5;
        }
        if (proposalData.bankInfo.iban) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('ibanLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const ibanText = safeText(proposalData.bankInfo.iban);
          const ibanLines = doc.splitTextToSize(ibanText, valueWidth);
          doc.text(ibanLines, valueX, bankTextY);
          bankTextY += Math.max(3.5, ibanLines.length * 3.5) + 0.5;
        }
        if (proposalData.bankInfo.swift) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('swiftLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const swiftText = safeText(proposalData.bankInfo.swift);
          const swiftLines = doc.splitTextToSize(swiftText, valueWidth);
          doc.text(swiftLines, valueX, bankTextY);
          bankTextY += Math.max(3.5, swiftLines.length * 3.5) + 0.5;
        }
        if (proposalData.bankInfo.branchName) {
          doc.setFont(getFont(), 'bold');
          doc.text(safeText(t('branchLabel')), bankBoxX + bankPadding, bankTextY);
          doc.setFont(getFont(), 'normal');
          const branchText = safeText(proposalData.bankInfo.branchName);
          const branchLines = doc.splitTextToSize(branchText, valueWidth);
          doc.text(branchLines, valueX, bankTextY);
        }
      }
      
      // Footer - right bottom corner
      const pageCount = doc.internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont(getFont(), 'normal');
        doc.setTextColor(...lightText);
        doc.text(
          `${t('page')} ${i} ${t('of')} ${pageCount}`,
          pageWidth - 10, // Right margin: 10mm from right edge
          pageHeight - 10, // Bottom margin: 10mm from bottom
          { align: 'right' }
        );
      }
      
      // Generate blob
      const pdfBlob = doc.output('blob');
      resolve(pdfBlob);
    } catch (error) {
      reject(error);
    }
  });
};
